import { Gift, Truck, Clock3, ShieldCheck, Sparkles, X } from "lucide-react";
import { useEffect } from "react";

interface FreeTrialModalProps {
  open: boolean;
  onClose: () => void;
  onStartTrial: () => void;
}

export default function FreeTrialModal({
  open,
  onClose,
  onStartTrial,
}: FreeTrialModalProps) {
  if (!open) return null;
  useEffect(() => {
    if (!open) return;

    const originalOverflow = document.body.style.overflow;

    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, [open]);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      {/* Floating Confetti */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        {[...Array(20)].map((_, i) => (
          <span
            key={i}
            className="absolute h-3 w-3 animate-bounce rounded-full bg-yellow-400 opacity-80"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 2}s`,
              animationDuration: `${1.5 + Math.random()}s`,
            }}
          />
        ))}
      </div>

      <div className="relative flex max-w-md flex-col overflow-hidden rounded-3xl bg-white shadow-2xl animate-[scaleIn_.35s_ease]">
        <button
          onClick={onClose}
          className="absolute right-4 top-4 z-20 rounded-full bg-white/20 p-2 text-white backdrop-blur transition hover:bg-white/30"
        >
          <X size={18} />
        </button>

        <div className="bg-gradient-to-br from-orange-500 via-red-500 to-yellow-500 px-6 py-6 text-center text-white">
          <Sparkles className="mx-auto mb-2 h-6 w-6 animate-pulse" />

          <img
            src="/mlblogo.jpeg"
            alt="Company Logo"
            className="mx-auto mb-3 h-16 w-16 rounded-full border-4 border-white bg-white object-contain shadow-lg"
          />

          <h2 className="text-2xl font-bold">
            🎉 Start Your Free Trial
          </h2>

          <p className="mt-2 text-sm text-white/90">
            Healthy lunches delivered fresh to your child's school.
          </p>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          <div className="space-y-4">

            <div className="flex items-start gap-3 rounded-xl bg-orange-50 p-4">
              <Truck className="mt-1 text-orange-500" size={20} />
              <div>
                <p className="font-semibold">Daily On-Time Delivery</p>
                <p className="text-sm text-gray-600">
                  Fresh lunch boxes delivered directly to school.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3 rounded-xl bg-blue-50 p-4">
              <ShieldCheck className="mt-1 text-blue-500" size={20} />
              <div>
                <p className="font-semibold">Safe & Hygienic</p>
                <p className="text-sm text-gray-600">
                  Packed with care to keep every meal fresh.
                </p>
              </div>
            </div>

            <div className="rounded-xl border border-yellow-200 bg-yellow-50 p-4">
              <div className="flex items-center gap-2">
                <Gift className="text-yellow-600" size={20} />
                <span className="font-semibold">
                  Enjoy your FREE trial before subscribing.
                </span>
              </div>
            </div>
          </div>
        </div>
        <div className="border-t bg-white p-5">
          <button
            onClick={onStartTrial}
            className="w-full rounded-xl bg-gradient-to-r from-orange-500 to-red-500 py-3 text-base font-semibold text-white shadow-lg transition hover:scale-[1.02]"
          >
            🚀 Start Free Trial
          </button>

          <p className="mt-3 text-center text-xs text-gray-500">
            No commitment • Quick signup • Cancel anytime
          </p>
        </div>
      </div>

      <style>{`
        @keyframes scaleIn {
          from {
            transform: scale(.8);
            opacity:0;
          }
          to {
            transform: scale(1);
            opacity:1;
          }
        }
      `}</style>
    </div>
  );
}